import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  SideBarDef,
} from "ag-grid-community";
import { useNavigate } from "react-router";

import { AgGridReact } from "ag-grid-react";
import { Dialog } from "@mui/material";
import _, { isEqual } from "lodash";
import { toast } from "react-toastify";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import { agConfigDashboardSideBar } from "../dashboardPage/AgGridConfig";
import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";

import { Box, HStack, useGetCompanyName } from "../../components/utils";
import GenericTableHeader from "../../shared/TableHeader";
import { ButtonSize, IconCTAButton, SecondaryCTAButton } from "../quickRound/CTAButtonComponents";
import QuickSearch from "../rounds/QuickSearch";
import { useAuthStore } from "../../store";
import {
  useGetOnFilterState,
  usePostOnFilterState,
} from "../dashboardPage/AgGridCacheQuery";
import { tableCellStyle } from "../../components/TableComponent";
import { useAgGridTableState } from "../../store/agGridTableStore";
import SearchInput from "../../components/shared/SearchInput";
import { AdjustedPrice, AllocationMethod, AntiDilutionMethod, SecurityDetails } from "./AntiDilutionSecurityAgGridComponent";
import { AntiDilutionAllocationType, AntiDilutionSecurity, PreviewQuickRoundResponseModel } from "../../types/previewQuickRound";
import { InstrumentClass } from "../../types/DashboardModel";
import UpdateAntiDilutionSecurityDetails from "../../modals/UpdateAntiDilutionSecurityDetails";
import Tooltip from "../../components/shared/Tooltip";
import { useUpdateAntDilutionDetails } from "../../queries/transactionRound";
import { QuickRoundTransactionModel } from "./RoundTransactionModel";
import { queryClient } from "../../queries";
import { useSecurityOverviewDetails } from "../../queries/securities";

function AGGridAntiDilutionSecurityTable({
  roundData,
  instrumentClasses,
  template,
  setTemplate,
  previewAntiDilution,
}: {
  roundData?: PreviewQuickRoundResponseModel;
  instrumentClasses: InstrumentClass[];
  template: QuickRoundTransactionModel;
  setTemplate: Function;
  previewAntiDilution?: boolean;
}) {
  const currencySymbol = getCurrencySymbol();
  const companyName = useGetCompanyName();
  const companyId = useAuthStore().companyId ?? "";
  const userId = useAuthStore().user?.userId ?? 0;
  const gridApi = useRef<any>(null);

  const displayedRowCount = gridApi.current?.api.getDisplayedRowCount();

  const { refetch, data: filterState } = useGetOnFilterState(
    `${userId}`,
    companyId
  );
  const agTableStore = useAgGridTableState();

  const { mutate: postOnFilter } = usePostOnFilterState();

  function getColumnSetting() {
    if (gridApi.current) return gridApi.current.columnApi?.getColumnState();
    return {};
  }

  function setColumnSetting(model: any) {
    if (gridApi.current)
      gridApi.current.columnApi.applyColumnState({ state: model });
    return {};
  }

  // Gets filter model via the grid API
  const getFilterSetting = () => {
    if (gridApi.current) return gridApi.current.api?.getFilterModel();
    return {};
  };

  const setFilterSetting = (model: any) => {
    if (gridApi.current) return gridApi.current.api?.setFilterModel(model);
    return {};
  };

  const uploadFilterAndColumn = async () => {
    const columnState = await getColumnSetting();
    const filterState = await getFilterSetting();
    postOnFilter({
      userId: `${userId}`,
      companyId,
      filterData: {
        ...agTableStore.state,
        shareHolderAgGridCaptableQuickRoundColumnModel: columnState,
        shareHolderAgGridCaptableQuickRoundFilterModel: filterState,
      },
    });
  };

  const cin = "";

  const componentsRegistery = useMemo(
    () => ({
      securityDetails: memo(SecurityDetails),
      allocationMethod: memo(AllocationMethod),
      adjustedPrice: memo(AdjustedPrice),
      antiDilutionMethod: memo(AntiDilutionMethod)
    }),
    []
  );

  const navigate = useNavigate();

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  );
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Security Name",
        field: "securityDetails",
        filter: "agMultiColumnFilter",
        getQuickFilterText: (params) => `${params.data.securityName} ${params.data.securityType}`,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Name",
              display: "subMenu",
              filter: "agTextColumnFilter",
              buttons: ["reset"],
              filterValueGetter: (params: any) => params.value.securityName,
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) =>
                  params.getValue("securityName"),
              },
            },
            {
              title: "Type",
              filter: "agSetColumnFilter",
              display: "subMenu",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                keyCreator: (params: any) => params.value.securityType,
                valueFormatter: (params: any) => params.value.securityType,
              },
            },
          ],
        },
        pinned: "left",
        cellRenderer: "securityDetails",
        initialWidth: 300,
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Anti-Dilution Method",
        field: "antiDilutionMethod",
        filter: "agSetColumnFilter",
        getQuickFilterText: (params) => params.data.antiDilutionMethod,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        cellRenderer: "antiDilutionMethod",
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Allocation Method",
        field: "allocationMethod",
        filter: "agSetColumnFilter",
        getQuickFilterText: (params) => params.data.allocationMethod,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        cellRenderer: "allocationMethod",
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: `Adjusted Price (${currencySymbol})`,
        field: "adjustedPrice",
        filter: "agSetColumnFilter",
        getQuickFilterText: (params) => params.data.adjustedPrice,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        cellRenderer: "adjustedPrice",
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Updated Conversion Ratio",
        field: "updatedConversionRatio",
        filter: "agSetColumnFilter",
        getQuickFilterText: (params) => params.data.updatedConversionRatio,
        comparator(valueA: any, valueB: any, ..._: any[]) {
          return valueA.name > valueB.name ? 1 : -1;
        },
        cellRenderer: "updatedConversionRatio",
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "",
        field: "action",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        menuTabs: [],
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        cellStyle: { paddingTop: "10px", lineHeight: "20px" },
        resizable: false,
        sortable: false,
        cellRendererParams: (value: any) =>
          value.data.action ? value.data.action.props : <div></div>,
        cellRenderer: CTADropdown,
      },
    ],
    []
  );

  const gridOptions = {
    suppressRowVirtualisation: true, // Add this line to disable row virtualization
    paginationAutoPageSize: false, // Optional: Set to false if using custom pagination
    suppressScrollOnNewData: true, // Optional: Set to true to prevent scrolling to the top on
  };

  const currencyType = getCurrencyType();

  const {
    data: securityDetail, refetch: getSecurities
  } = useSecurityOverviewDetails();

  useEffect(() => {
    getSecurities();
  }, []);

  function getAntiDilutionSecurities() {
    return instrumentClasses
    ?.filter((instrument) => instrument.subType !== "Options")
    ?.map((instrument) => ({
      instrumentClassId: instrument.id,
      adjustedPrice: 0,
      useConversionRatio: true,
      allocationMethod: "None",
      ratio: undefined,
      investorDetails: [],
    })) || [];
  }

  useEffect(() => {
    if (template.newRoundInput?.antiDilutionSecurities?.length !== 0) {
      setAntiDilutionSecurities(template.newRoundInput?.antiDilutionSecurities || []);
      return;
    }
    const initialValues = getAntiDilutionSecurities();
    const updatedDetails = (_.cloneDeep(initialValues) as
        AntiDilutionSecurity[]).filter((security: AntiDilutionSecurity) =>
        (securityDetail?.tableDetails
        ?.find((details) =>
          details.id === security?.instrumentClassId)?.shareHolder || []).length > 0
      );
    setAntiDilutionSecurities(
      updatedDetails
    );
    if (!_.isEqual(template.antiDilutionSecurities, updatedDetails)) {
      setTemplate((prev: any) => ({
        ...prev,
        antiDilutionSecurities: updatedDetails,
      }));
    }
  }, [securityDetail]);

  const [antiDilutionSecurities, setAntiDilutionSecurities] = useState<AntiDilutionSecurity[]>(
    []
  );

  useEffect(() => {
    if ((antiDilutionSecurities?.length
      || 0) > 0) {
      setTemplate((prev: any) => ({
        ...prev,
        antiDilutionSecurities,
      }));
    }
  }, [antiDilutionSecurities]);

  function handleUpdateAntiDilutionSecurity(
    securityDetails: AntiDilutionSecurity
  ) {
    const updatedDetails = _.cloneDeep(antiDilutionSecurities).map((security) => {
        if (security.instrumentClassId === securityDetails.instrumentClassId) {
          return securityDetails;
        }
        return security;
      });
    setAntiDilutionSecurities(
      updatedDetails
    );
    if (!_.isEqual(template.antiDilutionSecurities, updatedDetails)) {
      setTemplate((prev: any) => ({
        ...prev,
        antiDilutionSecurities: updatedDetails,
      }));
    }
  }

  const [dialog, setDialog] = useState<{
      open: boolean;
      data?: AntiDilutionSecurity;
      mode?: "Update" | "Delete" | "Add" | "View";
    }>({
      open: false,
    });

  function handleAction(
    data: AntiDilutionSecurity,
    action: Action
  ) {
    if (action.name === "Update") {
      setDialog({
        open: true,
        data,
        mode: "Update",
      });
    } else if (action.name === "View") {
      setDialog({
        open: true,
        data,
        mode: "View",
      });
    }
  }

  const rowData = useMemo(
    () =>
      antiDilutionSecurities?.map((template) => ({
        securityName: instrumentClasses.find((instrument) => instrument.id === template.instrumentClassId)?.name || "",
        securityType: instrumentClasses.find((instrument) => instrument.id === template.instrumentClassId)?.subType || "",
        allocationMethod:
          !template.allocationMethod ||
          template.allocationMethod === AntiDilutionAllocationType.None ?
            AntiDilutionAllocationType.None :
        template.useConversionRatio ?
          AntiDilutionAllocationType.UpdateConversionRatio :
          AntiDilutionAllocationType.IssueShares,
        adjustedPrice: template.adjustedPrice || "",
        updatedConversionRatio: template.ratio ?
          `1:${parseFloat((template.ratio || 1).toFixed(4))}` : "-",
        antiDilutionMethod:
          instrumentClasses.find((instrument) => instrument.id === template.instrumentClassId)?.antidilution || "None",
        action: (
          <CTADropdown
            actions={[
              {
                name: "Update",
                disabled: !previewAntiDilution,
              },
              {
                name: "View",
                disabled: false,
              },
            ]}
            onAction={(action) => handleAction(template, action)}
          />
        ),
        currencySymbol,
        currencyType,
      })),
    [antiDilutionSecurities, previewAntiDilution]
  );

  function setTableHeight() {
    if (displayedRowCount === 1) {
      return (displayedRowCount + 2.75) * 60;
    } else if (displayedRowCount === 2) {
      return (displayedRowCount + 2.68) * 60;
    } else if (displayedRowCount === 3) {
      return (displayedRowCount + 3) * 60;
    } else if (displayedRowCount === 4) {
      return (displayedRowCount + 2.55) * 60;
    } else if (displayedRowCount === 5) {
      return (displayedRowCount + 2.48) * 60;
    } else if (displayedRowCount === 6) {
      return (displayedRowCount + 2.42) * 60;
    } else if (displayedRowCount === 7) {
      return (displayedRowCount + 2.35) * 60;
    } else if (displayedRowCount === 8) {
      return (displayedRowCount + 2.28) * 60;
    } else if (displayedRowCount === 9) {
      return (displayedRowCount + 2.22) * 60;
    } else {
      return 10 * 60;
    }
  }

  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    setIsFilterApplied(filtersApplied);

    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
    uploadFilterAndColumn();
  };

  const { mutate: updateAntDilutionDetails } = useUpdateAntDilutionDetails();

  function updateSecurityConversionDetails(
    securityDetails: AntiDilutionSecurity
  ) {
    const updatedDetails = _.cloneDeep(antiDilutionSecurities).map((security) => {
        if (security.instrumentClassId === securityDetails.instrumentClassId) {
          return securityDetails;
        }
        return security;
      });
    setAntiDilutionSecurities(
      updatedDetails
    );
    if (!_.isEqual(template.antiDilutionSecurities, updatedDetails)) {
      setTemplate((prev: any) => ({
        ...prev,
        antiDilutionSecurities: updatedDetails,
      }));
    }
  }

  return (
    <div className="bg-white border rounded-md shadow-box border-borderColor py-5">
      <Dialog open={dialog.open} maxWidth="lg">
        {dialog.mode === "Update" ? (
          <UpdateAntiDilutionSecurityDetails
            initialValue={dialog.data}
            onPrimaryAction={(data: AntiDilutionSecurity) => {
              handleUpdateAntiDilutionSecurity(data);
              setDialog({ open: false });
            }}
            onSecondaryAction={() => setDialog({ open: false })}
            instrumentClasses={instrumentClasses}
            updateSecurityConversionDetails={updateSecurityConversionDetails}
            mode={"Update"}
          />
        ) :
        dialog.mode === "View" ? (
          <UpdateAntiDilutionSecurityDetails
            initialValue={dialog.data}
            onPrimaryAction={(data: AntiDilutionSecurity) => {
              handleUpdateAntiDilutionSecurity(data);
              setDialog({ open: false });
            }}
            onSecondaryAction={() => setDialog({ open: false })}
            instrumentClasses={instrumentClasses}
            mode={"View"}
          />
        ) : (<></>)
        }
        </Dialog>
      <HStack className="flex-col items-start justify-start py-2 lg:flex-row lg:justify-between lg:items-center">
        <GenericTableHeader
          heading={"Update Anti-Dilution Shares"}
          subHeading={"Update the anti-dilution shares for applicable securities"}
        />
        <HStack className="items-center justify-between w-full gap-4 px-8 py-2 lg:w-auto lg:justify-end">
          <SearchInput
            placeholder={`Search`}
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          />
          <HStack className="gap-4">
            <IconCTAButton
              value={"Columns"}
              onClick={() => openToolPanel("columns")}
              iconName={"fluent:column-triple-edit-20-regular"}
              className={`px-4 font-medium items-center flex flex-row ${
                isColumnOpen ? "text-orange-501" : "text-gray-400"
              }`}
            />
            <IconCTAButton
              value={"Filters"}
              onClick={() => openToolPanel("filters")}
              iconName={"material-symbols:filter-alt"}
              className={`px-4 font-medium items-center flex flex-row ${
                isFilterOpen ? "text-orange-501" : "text-gray-400"
              }`}
            />
          </HStack>
        </HStack>
      </HStack>
      <HStack className="justify-between w-full">
        <Box
          style={{
            height: setTableHeight(),
          }}
          className="w-full h-full max-h-full overflow-x-auto ag-theme-material "
        >
          <AgGridReact
            sideBar={agConfigDashboardSideBar}
            onGridReady={(params) => {
              // Store the grid API referen
              gridApi.current = params;
            }}
            components={componentsRegistery}
            alwaysShowHorizontalScroll={false}
            alwaysMultiSort
            animateRows={true}
            enableCharts={true}
            enableRangeSelection={true}
            defaultColDef={defaultColDef}
            onFilterChanged={onAgGridFilterChanged}
            onColumnEverythingChanged={uploadFilterAndColumn}
            rowData={rowData}
            columnDefs={columnDefs}
            pagination={false}
            suppressRowTransform={true}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            suppressMenuHide={false}
            onColumnResized={uploadFilterAndColumn}
            tooltipShowDelay={1000}
            tooltipInteraction={true}
            rowClass={"border-t border-dashed "}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
            }
            gridOptions={gridOptions}
            getRowStyle={(params) => {
              if (params.rowIndex % 2 === 0) {
                return { background: "#f8f8f8" };
              } else {
                return { background: "#ffffff" };
              }
            }}
          ></AgGridReact>
        </Box>
      </HStack>
      <HStack className="justify-end px-5 hidden">
        <Tooltip text={"Saving details will re-generate captable preview"}>
          <SecondaryCTAButton
            event-name="Cancel Founder Modal"
            buttonSize={ButtonSize.MEDIUM}
            onClick={() => {
              updateAntDilutionDetails({
                eventId: template.eventId || "",
                antiDilutionSecurities:
                  template.antiDilutionSecurities || [],
              }, {
                onSuccess: (data) => {
                  queryClient.invalidateQueries("proformaEventSummary");
                  queryClient.refetchQueries("proformaEventSummary");
                  toast("Anti-Dilution Details Updated Successfully!", {
                    type: "success",
                    autoClose: 2000,
                  });
                },
                onError: (err: any) => {
                  toast(err.response.data.errorMessage, {
                    type: "error",
                    autoClose: 2000,
                  });
                },
              });
            }}
          >
            Save Details
          </SecondaryCTAButton>
        </Tooltip>
      </HStack>
    </div>
  );
}

export default AGGridAntiDilutionSecurityTable;
